import React, { useRef, useState } from 'react';
import { DayPilotMonth } from '@daypilot/daypilot-lite-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  useCreateCalendarMonthYearMutation,
  useGetAllWorkCalendarsQuery,
  useUpdateCalendarWorkDayHourMutation,
} from 'src/app/services/workCalendarApi';
import DatePickerComponent from 'app/shared-components/datePickerComponent/datePickerComponent';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';

const WorkDayCalendar = () => {
  const { t, i18n } = useTranslation('translations');
  const { isDarkMode } = useSelector((state) => state.themeSlice);
  const monthRef = useRef();

  const [selectedDate, setSelectedDate] = useState(dayjs().startOf('month'));
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newHours, setNewHours] = useState('');

  const month = selectedDate.format('MM');
  const year = selectedDate.format('YYYY');

  const { data, refetch, isLoading } = useGetAllWorkCalendarsQuery({ year, month });
  const [updateHour] = useUpdateCalendarWorkDayHourMutation();
  const [createMonthCalendar] = useCreateCalendarMonthYearMutation();

  const handleEventClick = ({ e }) => {
    setEditingEvent(e.data);
    setNewHours(e.data.hours.toString());
    setModalOpen(true);
  };

  const handleModalSave = async () => {
    const hoursInt = parseInt(newHours, 10);
    if (!isNaN(hoursInt) && editingEvent && hoursInt !== editingEvent.hours) {
      await updateHour({
        id: editingEvent.id,
        date: editingEvent.start,
        hours: hoursInt,
      });
      refetch();
    }
    setModalOpen(false);
    setEditingEvent(null);
  };

  const generateEvents = (data = []) =>
    data.map((item) => ({
      id: item.id,
      start: item.date,
      end: item.date,
      text: `${item.hours} ${t('Hours')}`,
      hours: item.hours,
      backColor:
        item.hours === 0
          ? isDarkMode
            ? '#5c0000'
            : '#fc6262'
          : isDarkMode
          ? '#2a3b4d'
          : '#3dcfff',
      fontColor: isDarkMode ? '#ffffff' : '#000000',
    }));

  const handleDateChange = (newDate) => {
    if (newDate && dayjs.isDayjs(newDate)) {
      setSelectedDate(newDate.startOf('month'));
    }
  };

  return (
    <div className="p-4 grid gap-4">
      <div className="flex justify-between items-center w-full mb-16 mt-16">
        <h2 className="ml-5 mb-10">{t('WorkCalendar')}</h2>
        <DatePickerComponent value={selectedDate} onChange={handleDateChange} />
      </div>
      <div className="border rounded-lg shadow p-4 relative">
        <DayPilotMonth
          key={`${year}-${month}-${i18n.language}`}
          ref={monthRef}
          locale={i18n.language === 'ru' ? 'ru-ru' : 'uz-uz'}
          theme={isDarkMode ? 'dark' : 'lightblue'}
          startDate={`${year}-${month}-01`}
          timeRangeSelectedHandling="Disabled"
          eventClickHandling="Enabled"
          onEventClick={handleEventClick}
          eventMoveHandling="Disabled"
          eventResizeHandling="Disabled"
          events={{ list: generateEvents(data || []) }}
        />
        {!data?.length && !isLoading && (
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-lightgrey/70 dark:bg-darkergrey/40 flex justify-center items-center z-50">
            <Button
              onClick={() => {
                createMonthCalendar({ month: month, year: year });
              }}
              className="bg-blue-800 text-white hover:bg-blue-400 px-28"
            >
              {t('Create')}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>{t('EditWorkHour')}</DialogTitle>
        <DialogContent>
          <TextField
            type="number"
            value={newHours}
            onChange={(e) => setNewHours(e.target.value)}
            fullWidth
            autoFocus
            inputProps={{ min: 0, max: 24 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="inherit">
            {t('Cancel')}
          </Button>
          <Button onClick={handleModalSave} variant="outlined">
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WorkDayCalendar;
